import { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { MaskedLine } from "@/components/Reveal";
import { useLenis, scrollToTarget } from "@/components/LenisProvider";
import { Button } from "@/components/ui/button";

const HERO_IMG =
  "https://images.unsplash.com/photo-1774806270948-4edf892c20b7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2OTV8MHwxfHNlYXJjaHwyfHxjcmFmdCUyMGNvY2t0YWlsJTIwZGFyayUyMGJhY2tncm91bmR8ZW58MHx8fHwxNzkxMjE2MzY3fDA&ixlib=rb-4.1.0&q=85";

const META = ["Professional bartenders", "Custom cocktail menus", "Fully mobile — any venue"];

export default function Hero() {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const lenis = useLenis();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);

  return (
    <section ref={ref} id="top" className="relative min-h-screen flex flex-col justify-end overflow-hidden">
      <motion.div
        style={reduce ? {} : { y: bgY, scale: bgScale }}
        className="absolute inset-0 will-change-transform"
        data-testid="hero-parallax-image"
      >
        <img
          src={HERO_IMG}
          alt="Smoked craft cocktail under a glass dome"
          className="h-full w-full object-cover opacity-75"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-[#090909] via-[#090909]/35 to-[#090909]/55" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#090909]/80 via-transparent to-transparent" />

      <div className="relative z-10 mx-auto w-full max-w-7xl px-5 md:px-10 pb-16 md:pb-24 pt-40">
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="eyebrow mb-6"
        >
          Mobile Bartending — Book Your Date
        </motion.p>

        <h1 className="font-display text-[#F5F5F5] leading-[0.95] tracking-tight text-6xl sm:text-7xl lg:text-8xl max-w-5xl">
          <MaskedLine delay={0.2}>The bar,</MaskedLine>
          <MaskedLine delay={0.38}>
            <em className="italic text-[#D98B48]">brought</em> to you.
          </MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.75 }}
          className="mt-7 max-w-xl text-base md:text-lg text-[#A3A3A3] font-light"
        >
          Full craft-cocktail service for weddings, private parties and brand
          events. We bring the bartenders, the menu and the ice — you pick the
          venue.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.9 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <Button
            data-testid="hero-book-button"
            onClick={() => scrollToTarget(lenis, "#booking")}
            className="rounded-sm bg-[#D98B48] hover:bg-[#F0A562] text-[#090909] font-medium px-8 py-6 text-sm"
          >
            Book your date
          </Button>
          <Button
            data-testid="hero-packages-button"
            variant="outline"
            onClick={() => scrollToTarget(lenis, "#packages")}
            className="rounded-sm border-white/20 bg-transparent text-[#F5F5F5] hover:bg-white/10 hover:text-[#F5F5F5] px-8 py-6 text-sm transition-colors"
          >
            Explore packages
          </Button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.15 }}
          className="mt-14 flex flex-wrap gap-x-8 gap-y-3 text-xs uppercase tracking-[0.2em] text-[#A3A3A3]"
        >
          {META.map((m) => (
            <span key={m} className="inline-flex items-center gap-3">
              <span className="h-1 w-1 rotate-45 bg-[#C5A059] inline-block" />
              {m}
            </span>
          ))}
        </motion.div>
      </div>

      <motion.div
        animate={{ y: [0, 8, 0] }}
        transition={{ repeat: Infinity, duration: 2.4, ease: "easeInOut" }}
        className="absolute bottom-6 right-6 md:right-10 z-10 text-[#A3A3A3]"
        aria-hidden="true"
      >
        <ArrowDown className="h-5 w-5" />
      </motion.div>
    </section>
  );
}
